/**
 * Character builder parts — body shapes, hat shapes, face presets.
 *
 * Each shape is hand-rolled SVG path on a 100x100 canvas. Bodies fill
 * roughly the bottom-center; hats sit above ~y=20; eyes/mouth around
 * y=45-65. Player picks one body, one hat (or "none"), one face, plus
 * a body hue and an accent hue.
 *
 * Phase E1 — added BORDERS and BACKGROUNDS registries (empty initially;
 * E3 fills them). Each entry has an `id` and a `render` function that
 * returns declarative SVG children + an optional CSS className for
 * animations. IconAvatar consumes the registries to draw the optional
 * border + background layers.
 */

import type { ReactNode } from 'react';

export interface BodyShape {
  id: string;
  label: string;
  body: string;        // path filling the body silhouette
  /** Optional decorative SVG path drawn ON TOP of the body fill (after
   *  stroke). Painted with `decorationFill` if provided, else accent.
   *  Phase E3 — used by Solstice Bearer's pulsing ember. */
  decorationPath?: string;
  /** Optional CSS className applied to the wrapping <g> around the
   *  decoration path so it can animate via the cosmetic-animations
   *  section in global.css. */
  decorationClass?: string;
  /** Hard-coded fill for the decoration (overrides accent). Used by
   *  Crimson Solstice items that must render in the fixed palette
   *  regardless of player hue. */
  decorationFill?: string;
}

export interface HatShape {
  id: string;
  label: string;
  path: string;
  accent?: string;     // optional second path for inner detail
  /** Phase E3 — optional CSS className on the accent <path> wrapper so
   *  it can animate (e.g. Crown of Coals' hue-shifting gems). */
  accentClass?: string;
  /** Phase E3 — fixed fill for accent path (overrides body-accent
   *  derivation). Used by Crimson Solstice items. */
  accentFill?: string;
  /** Phase E3 — fixed fill for the main hat path. Crimson Solstice
   *  items use this so they render in palette regardless of hue. */
  baseFill?: string;
}

export interface FaceShape {
  id: string;
  label: string;
  eyes: Array<{ x: number; y: number; r: number }>;
  mouth?: string;       // path for mouth
  blush?: [number, number, number]; // [leftX, y, rightX] cheek blush positions
  /** Phase E3 — optional fixed fill for eyes (overrides feature color).
   *  Crimson Solstice's Searing Mark uses crimson-glowing eyes. */
  eyeFill?: string;
  /** Phase E3 — optional CSS className on the eyes <g> for animation. */
  eyeClass?: string;
  /** Phase E3 — optional decorative path drawn over the face (e.g. the
   *  vertical gold scar on Searing Mark). */
  markPath?: string;
  markFill?: string;
}

export interface CharacterIconExt {
  set: string;
  id: number;
  hue: number;
  body: number;
  hat: number;
  face: number;
  accentHue?: number;
  paletteId?: string;
  /** Phase E1 — avatar-frame cosmetic id; resolves in BORDERS. */
  border?: string;
  /** Phase E1 — avatar-backdrop cosmetic id; resolves in BACKGROUNDS. */
  background?: string;
  /** Phase F5 — face-overlay mask cosmetic id; resolves in MASKS.
   *  Renders ON TOP of face features but UNDER the hat. */
  mask?: string;
}

export const BODY_SHAPES: BodyShape[] = [
  // 0 round blob
  { id: 'blob', label: 'Blob',
    body: 'M50 22 C 26 22, 16 46, 22 66 C 26 84, 42 92, 50 92 C 58 92, 74 84, 78 66 C 84 46, 74 22, 50 22 Z' },
  // 1 tall
  { id: 'tall', label: 'Tall',
    body: 'M50 16 C 32 16, 28 32, 30 50 L 30 78 C 30 88, 70 88, 70 78 L 70 50 C 72 32, 68 16, 50 16 Z' },
  // 2 cat-like (with ears)
  { id: 'cat', label: 'Cat',
    body: 'M30 28 L 34 14 L 42 26 C 47 24, 53 24, 58 26 L 66 14 L 70 28 C 80 36, 82 64, 70 80 C 60 92, 40 92, 30 80 C 18 64, 20 36, 30 28 Z' },
  // 3 bear-ear
  { id: 'bear', label: 'Bear',
    body: 'M28 32 C 18 24, 24 14, 32 16 C 36 18, 38 22, 38 26 C 44 22, 56 22, 62 26 C 62 22, 64 18, 68 16 C 76 14, 82 24, 72 32 C 84 44, 84 76, 70 84 C 58 92, 42 92, 30 84 C 16 76, 16 44, 28 32 Z' },
  // 4 tall round
  { id: 'pumpkin', label: 'Pumpkin',
    body: 'M50 18 C 22 22, 18 50, 24 70 C 28 86, 42 92, 50 92 C 58 92, 72 86, 76 70 C 82 50, 78 22, 50 18 Z M 50 18 L 50 8' },
  // 5 ghost flowy
  { id: 'ghost', label: 'Ghost',
    body: 'M50 18 C 28 18, 22 36, 22 56 L 22 86 C 22 90, 28 92, 30 88 L 36 84 C 38 90, 44 90, 46 84 L 50 86 C 52 90, 58 90, 60 84 L 66 88 C 68 92, 72 90, 72 86 L 72 56 C 72 36, 72 18, 50 18 Z' },
  // 6 robot block
  { id: 'robot', label: 'Robot',
    body: 'M28 28 L 72 28 L 76 80 C 76 88, 70 92, 64 92 L 36 92 C 30 92, 24 88, 24 80 Z' },
  // 7 mushroom
  { id: 'mushroom', label: 'Mushroom',
    body: 'M50 18 C 22 18, 18 50, 24 56 L 76 56 C 82 50, 78 18, 50 18 Z M 38 56 L 38 88 C 38 92, 62 92, 62 88 L 62 56 Z' },
  // 8 solstice-bearer-body — Crimson Solstice premium signature (E3,
  // tier 20). Flame-shaped silhouette painted in the fixed Solstice
  // palette (#4a0a0a → #ff4040). A pulsing orange ember sits at the
  // heart, animated via .solstice-ember (cf. global.css cosmetic
  // animations). decorationFill #ffaa3d = flame-orange.
  { id: 'solstice-bearer-body', label: 'Solstice Bearer',
    body: 'M50 8 C 44 22, 30 30, 32 48 C 26 50, 24 60, 30 70 C 26 78, 32 92, 50 92 C 68 92, 74 78, 70 70 C 76 60, 74 50, 68 48 C 70 30, 56 22, 50 8 Z',
    decorationPath: 'M50 56 m -7 0 a 7 9 0 1 0 14 0 a 7 9 0 1 0 -14 0',
    decorationClass: 'solstice-ember',
    decorationFill: '#ffaa3d' },
  // ── Phase E2 new bodies (indices 9-18) ──────────────────────────────
  // Each body has at least 2 layers: silhouette (body) + decoration
  // path. Decorations tint via accentFill unless overridden with a
  // fixed decorationFill (used for items where the decoration semantics
  // demand a specific color, e.g. flame must read as fire). Animations
  // live in global.css under the "Cosmetic animations" section,
  // applied via the wrapping <g>'s decorationClass.

  // 9 fox — angular silhouette with two pointed ears and a tail
  // arcing to the side. Decoration: chest blaze + tail-tip highlight.
  { id: 'fox', label: 'Fox',
    body: 'M30 30 L 24 12 L 34 26 C 40 22, 60 22, 66 26 L 76 12 L 70 30 C 78 38, 80 64, 74 80 C 70 90, 60 92, 50 88 C 40 92, 30 90, 26 80 C 20 64, 22 38, 30 30 Z M 72 76 C 84 76, 90 60, 86 50 C 86 64, 80 70, 72 72 Z',
    decorationPath: 'M46 50 L 50 78 L 54 50 Z M 80 56 L 86 52 L 84 60 Z',
    decorationClass: 'body-fox' },
  // 10 wolf — broader silhouette with thick mane tufts. Two angular
  // ears at the top, a heavy neck spreading to a wide chest.
  { id: 'wolf', label: 'Wolf',
    body: 'M50 24 C 32 24, 24 36, 24 50 C 18 54, 18 62, 22 64 C 22 78, 32 90, 50 90 C 68 90, 78 78, 78 64 C 82 62, 82 54, 76 50 C 76 36, 68 24, 50 24 Z M 30 36 L 24 22 L 30 30 Z M 70 36 L 76 22 L 70 30 Z',
    decorationPath: 'M34 56 L 30 72 L 38 64 L 42 78 L 46 64 L 50 80 L 54 64 L 58 78 L 62 64 L 70 72 L 66 56 Z',
    decorationClass: 'body-wolf' },
  // 11 raven — winged perched bird. Ovoid body with two outstretched
  // wing tufts. Decoration: feather streaks across the wings.
  { id: 'raven', label: 'Raven',
    body: 'M50 28 C 40 28, 34 38, 34 54 C 34 76, 42 90, 50 90 C 58 90, 66 76, 66 54 C 66 38, 60 28, 50 28 Z M 34 50 L 14 44 L 22 54 L 12 60 L 24 64 L 16 74 L 30 70 Z M 66 50 L 86 44 L 78 54 L 88 60 L 76 64 L 84 74 L 70 70 Z M 50 28 L 46 20 L 50 24 L 54 20 Z',
    decorationPath: 'M22 52 L 18 56 L 18 54 Z M 24 60 L 20 64 L 20 62 Z M 26 66 L 22 70 L 22 68 Z M 78 52 L 82 56 L 82 54 Z M 76 60 L 80 64 L 80 62 Z M 74 66 L 78 70 L 78 68 Z',
    decorationClass: 'body-raven' },
  // 12 owl — round body with two large pale eye-discs integrated into
  // the silhouette. Two small ear tufts at the top. The decoration
  // paints the eye discs in a near-white tone (decorationFill) so the
  // owl reads as eyes-on-the-body rather than relying on the face
  // layer.
  { id: 'owl', label: 'Owl',
    body: 'M50 22 C 32 22, 24 38, 24 56 C 24 78, 36 92, 50 92 C 64 92, 76 78, 76 56 C 76 38, 68 22, 50 22 Z M 30 30 L 26 18 L 32 26 Z M 70 30 L 74 18 L 68 26 Z',
    decorationPath: 'M40 50 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 M 60 50 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 M 47 56 L 50 62 L 53 56 Z',
    decorationFill: '#f3e9d2',
    decorationClass: 'body-owl' },
  // 13 jellyfish — translucent bell with trailing tendril fringe.
  // Decoration: tendril paths drawn as thin tapered fills hanging
  // from the bell. CSS-animated float (translateY ±2px on the body).
  { id: 'jellyfish', label: 'Jellyfish',
    body: 'M50 24 C 28 24, 20 44, 22 60 C 22 64, 28 64, 30 60 C 32 56, 36 64, 38 60 C 40 56, 44 64, 46 60 C 48 56, 52 64, 54 60 C 56 56, 60 64, 62 60 C 64 56, 68 64, 70 60 C 72 64, 78 64, 78 60 C 80 44, 72 24, 50 24 Z',
    decorationPath: 'M28 60 L 30 62 L 26 88 L 24 86 Z M 38 60 L 40 62 L 38 92 L 36 90 Z M 50 60 L 52 62 L 50 92 L 48 92 Z M 62 60 L 64 62 L 64 90 L 60 92 Z M 72 60 L 74 62 L 76 86 L 72 88 Z',
    decorationClass: 'body-jellyfish' },
  // 14 living-candle — wax cylinder body with a flickering flame on
  // top. Decoration: orange flame painted in fixed flame color
  // (#ffaa3d) so it reads as fire regardless of body hue. The flame
  // pulses via .body-living-candle (opacity wave).
  { id: 'living-candle', label: 'Living Candle',
    body: 'M38 30 C 38 26, 62 26, 62 30 L 62 88 C 62 92, 38 92, 38 88 Z M 42 30 C 42 26, 58 22, 58 30 Z',
    decorationPath: 'M50 12 C 44 18, 56 22, 50 30 C 50 22, 44 18, 50 12 Z M 48 30 L 52 30 L 52 36 L 48 36 Z',
    decorationFill: '#ffaa3d',
    decorationClass: 'body-living-candle' },
  // 15 lantern — paper lantern with a handle hoop on top. Decoration:
  // warm amber glow square in the lantern's center + the top brace.
  // Animated glow (drop-shadow pulse via .body-lantern).
  { id: 'lantern', label: 'Lantern',
    body: 'M30 38 C 30 30, 70 30, 70 38 L 72 70 C 72 80, 60 88, 50 88 C 40 88, 28 80, 28 70 Z M 34 30 L 50 14 L 66 30 Z M 50 14 L 50 6',
    decorationPath: 'M40 48 L 60 48 L 60 70 L 40 70 Z M 38 38 L 62 38 L 62 42 L 38 42 Z',
    decorationFill: '#ffd27a',
    decorationClass: 'body-lantern' },
  // 16 broom — witch's broom: cone-bristle bottom with a long handle
  // extending upward. Decoration: binding twine wrap + sweeping
  // bristle stripes across the bristle cone.
  { id: 'broom', label: 'Broom',
    body: 'M48 12 L 52 12 L 52 56 L 64 56 L 76 92 L 24 92 L 36 56 L 48 56 Z',
    decorationPath: 'M34 60 L 66 60 L 64 64 L 36 64 Z M 32 92 L 38 64 L 36 64 Z M 48 92 L 50 64 L 52 92 Z M 68 92 L 64 64 L 62 64 Z',
    decorationClass: 'body-broom' },
  // 17 cauldron — squat round pot with rim handles + bubbling liquid.
  // Decoration: bubble circles rising from the cauldron's open top.
  { id: 'cauldron', label: 'Cauldron',
    body: 'M22 52 C 22 46, 78 46, 78 52 L 76 84 C 76 90, 24 90, 24 84 Z M 18 50 L 22 46 L 22 54 Z M 78 50 L 82 50 L 78 54 Z M 26 48 L 74 48 L 74 56 L 26 56 Z',
    decorationPath: 'M34 44 m -3 0 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0 M 50 38 m -4 0 a 4 4 0 1 0 8 0 a 4 4 0 1 0 -8 0 M 64 42 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0',
    decorationClass: 'body-cauldron' },
  // 18 scroll — rolled parchment with rolled top + bottom and a
  // ribbon tie running across. Decoration: ink-line text + ribbon.
  { id: 'scroll', label: 'Scroll',
    body: 'M32 22 C 32 18, 68 18, 68 22 L 68 26 C 68 28, 64 30, 60 28 L 60 78 C 64 76, 68 78, 68 82 L 68 86 C 68 90, 32 90, 32 86 L 32 82 C 32 78, 36 76, 40 78 L 40 28 C 36 30, 32 28, 32 26 Z',
    decorationPath: 'M44 48 L 56 48 L 56 52 L 44 52 Z M 44 60 L 56 60 L 56 64 L 44 64 Z M 38 36 L 62 36 L 62 40 L 38 40 Z',
    decorationClass: 'body-scroll' },
];

export const HAT_SHAPES: HatShape[] = [
  // 0 = none
  { id: 'none', label: 'None', path: '' },
  // 1 wizard
  { id: 'wizard', label: 'Wizard',
    path: 'M50 4 L 26 32 L 74 32 Z',
    accent: 'M44 22 L 50 16 L 56 22 Z' },
  // 2 crown
  { id: 'crown', label: 'Crown',
    path: 'M28 28 L 32 12 L 40 22 L 50 8 L 60 22 L 68 12 L 72 28 Z',
    accent: 'M30 24 L 70 24 L 70 28 L 30 28 Z' },
  // 3 top hat
  { id: 'top', label: 'Top hat',
    path: 'M30 28 L 70 28 L 70 24 L 64 24 L 64 6 L 36 6 L 36 24 L 30 24 Z' },
  // 4 hood
  { id: 'hood', label: 'Hood',
    path: 'M22 32 C 22 12, 78 12, 78 32 L 76 36 C 60 30, 40 30, 24 36 Z' },
  // 5 antlers
  { id: 'antlers', label: 'Antlers',
    path: 'M30 22 L 22 6 L 28 14 L 32 4 L 36 18 Z M 64 18 L 68 4 L 72 14 L 78 6 L 70 22 Z' },
  // 6 flower
  { id: 'flower', label: 'Flower',
    path: 'M50 6 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 M50 16 L50 22',
    accent: 'M50 8 a 4 4 0 1 0 0 0.001' },
  // 7 candle
  { id: 'candle', label: 'Candle',
    path: 'M46 24 L 54 24 L 54 14 L 46 14 Z',
    accent: 'M50 14 C 46 8, 54 4, 50 -2 C 50 4, 46 10, 50 14 Z' },
  // 8 crown-of-coals — Crimson Solstice premium signature (E3, tier 40).
  // Low brass-gold circlet (#ddc090) with three glowing red gems
  // (#ff4040). The gems hue-shift via .solstice-gem.
  { id: 'crown-of-coals', label: 'Crown of Coals',
    path: 'M28 30 L 30 18 L 38 26 L 50 14 L 62 26 L 70 18 L 72 30 Z',
    accent: 'M40 24 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0 M50 18 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0 M60 24 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0',
    accentClass: 'solstice-gem',
    accentFill: '#ff4040',
    baseFill: '#ddc090' },
  // ── Phase E2 new hats (indices 9-14) ────────────────────────────────

  // 9 tricorn — three-pointed pirate hat. Wide curved brim with three
  // upturned points. Accent: gold-trim band on the front.
  { id: 'tricorn', label: 'Tricorn',
    path: 'M14 32 C 14 18, 28 8, 50 8 C 72 8, 86 18, 86 32 L 78 28 L 70 14 L 60 24 L 50 6 L 40 24 L 30 14 L 22 28 Z',
    accent: 'M22 26 L 78 26 L 78 30 L 22 30 Z' },
  // 10 witch — tall pointed cone with wide circular brim. Accent: a
  // hat-band with buckle suggestion at the base of the cone.
  { id: 'witch', label: 'Witch',
    path: 'M50 0 L 38 28 C 36 32, 64 32, 62 28 Z M 14 32 C 14 28, 86 28, 86 32 C 86 36, 14 36, 14 32 Z',
    accent: 'M40 28 L 60 28 L 60 32 L 40 32 Z M 48 30 L 52 30 L 52 32 L 48 32 Z' },
  // 11 brass-goggles — circular brass-strapped goggles worn on the
  // forehead. Two lens circles connected by a strap; accent: rivets.
  { id: 'brass-goggles', label: 'Brass Goggles',
    path: 'M36 22 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0 M 64 22 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0 M 46 20 L 54 20 L 54 24 L 46 24 Z M 14 22 L 26 22 L 26 24 L 14 24 Z M 74 22 L 86 22 L 86 24 L 74 24 Z',
    accent: 'M36 22 m -5 0 a 5 5 0 1 0 10 0 a 5 5 0 1 0 -10 0 M 64 22 m -5 0 a 5 5 0 1 0 10 0 a 5 5 0 1 0 -10 0' },
  // 12 antlers — branched deer antlers, more elaborate than HAT_SHAPES[5]'s
  // simple V-prongs. Each side has 3 forks at staggered heights.
  { id: 'antler-circlet', label: 'Antler Circlet',
    path: 'M30 28 L 22 12 L 26 18 L 16 6 L 22 16 L 14 14 L 26 24 L 18 22 L 32 30 Z M 70 28 L 78 12 L 74 18 L 84 6 L 78 16 L 86 14 L 74 24 L 82 22 L 68 30 Z M 30 28 L 70 28 L 70 30 L 30 30 Z',
    accent: 'M32 28 L 68 28 L 68 30 L 32 30 Z' },
  // 13 halo — glowing ring above the head. Accent ring is the bright
  // inner glow band; CSS animation .hat-halo pulses the outer glow.
  { id: 'halo', label: 'Halo',
    path: 'M50 16 m -18 0 a 18 6 0 1 0 36 0 a 18 6 0 1 0 -36 0 M 50 16 m -14 0 a 14 4 0 1 0 28 0 a 14 4 0 1 0 -28 0',
    accent: 'M50 16 m -10 0 a 10 3 0 1 0 20 0 a 10 3 0 1 0 -20 0',
    accentClass: 'hat-halo',
    accentFill: '#fff4c2' },
  // 14 candle-cap — a single tall candle worn as a hat. Accent: tiny
  // flame at the top. Distinct from BODY_SHAPES living-candle (which
  // is the entire avatar).
  { id: 'candle-cap', label: 'Candle Cap',
    path: 'M44 30 L 56 30 L 56 14 L 44 14 Z M 38 30 L 62 30 L 62 32 L 38 32 Z',
    accent: 'M50 14 C 46 8, 54 4, 50 -2 C 50 4, 46 10, 50 14 Z',
    accentClass: 'hat-candle-cap',
    accentFill: '#ffaa3d' },
];

export const FACE_SHAPES: FaceShape[] = [
  // 0 dot eyes + smile
  { id: 'happy', label: 'Happy',
    eyes: [{ x: 40, y: 50, r: 2.4 }, { x: 60, y: 50, r: 2.4 }],
    mouth: 'M40 64 Q 50 72 60 64', blush: [33, 60, 67] },
  // 1 sleepy eyes
  { id: 'sleepy', label: 'Sleepy',
    eyes: [{ x: 40, y: 52, r: 1.4 }, { x: 60, y: 52, r: 1.4 }],
    mouth: 'M44 66 Q 50 64 56 66' },
  // 2 wide eyes surprised
  { id: 'wide', label: 'Wide',
    eyes: [{ x: 40, y: 50, r: 3.2 }, { x: 60, y: 50, r: 3.2 }],
    mouth: 'M46 66 Q 50 70 54 66' },
  // 3 angry slits
  { id: 'angry', label: 'Stern',
    eyes: [{ x: 40, y: 50, r: 1.8 }, { x: 60, y: 50, r: 1.8 }],
    mouth: 'M42 66 L 58 66' },
  // 4 cute round
  { id: 'cute', label: 'Cute',
    eyes: [{ x: 40, y: 50, r: 2.8 }, { x: 60, y: 50, r: 2.8 }],
    mouth: 'M46 66 Q 50 70 54 66', blush: [32, 60, 68] },
  // 5 mischievous
  { id: 'mischief', label: 'Mischief',
    eyes: [{ x: 40, y: 50, r: 2 }, { x: 60, y: 50, r: 2 }],
    mouth: 'M40 64 Q 45 70 50 66 Q 55 62 60 68' },
  // 6 sad
  { id: 'sad', label: 'Sad',
    eyes: [{ x: 40, y: 50, r: 2 }, { x: 60, y: 50, r: 2 }],
    mouth: 'M40 70 Q 50 62 60 70' },
  // 7 cyclops
  { id: 'cyclops', label: 'Cyclops',
    eyes: [{ x: 50, y: 50, r: 4.4 }],
    mouth: 'M44 66 Q 50 70 56 66' },
  // 8 searing-mark — Crimson Solstice premium signature (E3, tier 60,
  // mythic). Crimson glowing eyes that subtly blink via .solstice-eye,
  // plus a vertical brass-gold scar (#ddc090) running across the face.
  { id: 'searing-mark', label: 'Searing Mark',
    eyes: [{ x: 40, y: 50, r: 2.8 }, { x: 60, y: 50, r: 2.8 }],
    mouth: 'M42 66 L 58 66',
    eyeFill: '#ff4040',
    eyeClass: 'solstice-eye',
    markPath: 'M48 36 L 52 36 L 52 72 L 48 72 Z',
    markFill: '#ddc090' },
  // ── Phase E2 new faces (indices 9-12) ───────────────────────────────

  // 9 single-eye — a giant centered cyclops eye, more pronounced than
  // FACE_SHAPES[7]'s "Cyclops". Includes a small iris-mark beneath
  // (markPath drawn over the face).
  { id: 'single-eye', label: 'Single Eye',
    eyes: [{ x: 50, y: 50, r: 6.4 }],
    mouth: 'M44 68 Q 50 72 56 68',
    markPath: 'M48 48 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0',
    markFill: '#1a1410' },
  // 10 third-eye — three eyes in a triangle pattern. The middle/upper
  // eye is decorated as a "third eye" via markPath glow.
  { id: 'third-eye', label: 'Third Eye',
    eyes: [{ x: 38, y: 52, r: 2.2 }, { x: 62, y: 52, r: 2.2 }, { x: 50, y: 40, r: 2.6 }],
    mouth: 'M42 66 Q 50 70 58 66',
    markPath: 'M50 40 m -4 0 a 4 4 0 1 0 8 0 a 4 4 0 1 0 -8 0',
    markFill: '#a070ff',
    eyeClass: 'face-third-eye' },
  // 11 calligraphic — stylized brushstroke eyes (curved ink strokes
  // instead of round dots). Eyes array is empty; the markPath carries
  // the brushstroke glyphs so they render in feature color.
  { id: 'calligraphic', label: 'Calligraphic',
    eyes: [],
    mouth: 'M40 66 Q 50 72 60 66',
    markPath: 'M34 48 Q 38 44 44 48 Q 40 52 34 50 Z M 56 48 Q 60 44 66 48 Q 62 52 56 50 Z',
    markFill: '#1a1410' },
  // 12 ghost-veil — hollow eye sockets with a faint luminous glow
  // (eyeFill near-white, eyeClass animates a pulse). No mouth.
  { id: 'ghost-veil', label: 'Ghost Veil',
    eyes: [{ x: 40, y: 50, r: 3 }, { x: 60, y: 50, r: 3 }],
    eyeFill: '#e8eaff',
    eyeClass: 'face-ghost-veil',
    markPath: 'M40 50 m -1 0 a 1 1 0 1 0 2 0 a 1 1 0 1 0 -2 0 M 60 50 m -1 0 a 1 1 0 1 0 2 0 a 1 1 0 1 0 -2 0',
    markFill: '#1a1410' },
];

export const HUES = [0, 18, 25, 35, 50, 90, 110, 130, 140, 180, 210, 240, 250, 270, 290, 320, 330];
//                      ^new   ^new                ^new                ^new
// 110 / 240 / 320 are shop-exclusive hues (Mint / Indigo / Orchid)
// added in the customization revamp Phase 1. Without them the
// IconPicker doesn't render swatches for the items even when they're
// in the player's inventory.
//
// Phase E2 — added Sunset (18), Bone (35), Forest (130), Twilight (270)
// as four new accent hue swatches. Catalog entries (`accent-18`,
// `accent-35`, `accent-130`, `accent-270`) live in COSMETIC_CATALOG
// with mixed embers-shop / lantern-shop sources.

// ─────────────────────────────────────────────────────────────────────
// Phase E1 — Borders + Backgrounds (avatar-icon decoration)
// ─────────────────────────────────────────────────────────────────────
//
// IconAvatar renders these layers BACK→FRONT in this order:
//   background (rect/g filling the 100x100 frame, BEHIND body) →
//   body silhouette → face features → hat → border ring (stroked
//   circle/path drawn LAST so it always reads as a frame).
//
// Each registry is empty in E1 — E3 appends real entries. Both render
// functions receive the avatar's primary + accent hue so themed items
// can tint themselves to match the wearer (e.g. an "ember" border that
// picks up the player's body color). The optional `className` becomes
// the wrapping <g>'s className so CSS keyframes can animate the layer
// without a per-avatar requestAnimationFrame loop.

/** Render arguments shared by border + background entries. */
export interface CosmeticLayerOpts {
  /** Avatar's primary body hue (0-359). Resolved from palette or fallback. */
  primaryHue: number;
  /** Avatar's accent hue (0-359). Resolved from palette or fallback. */
  accentHue: number;
}

/** What a layer's render fn returns. `children` is the SVG fragment
 *  (one or many elements) appended inside a wrapping <g>; `className`
 *  is attached to that <g> so CSS animations can target the whole
 *  layer at once. */
export interface CosmeticLayerOutput {
  children: ReactNode;
  className?: string;
}

export interface BorderDef {
  id: string;
  /** Render the border. Returns SVG children to append into a wrapping
   *  <g>, plus optional className for CSS animation. The wrapping <g>
   *  is positioned ON TOP of all other layers (final overlay). */
  render(opts: CosmeticLayerOpts): CosmeticLayerOutput;
}

export interface BackgroundDef {
  id: string;
  /** Render the background. Returns SVG children to append into a
   *  wrapping <g>, plus optional className for CSS animation. The
   *  wrapping <g> is positioned BEHIND the body silhouette. */
  render(opts: CosmeticLayerOpts): CosmeticLayerOutput;
}

/** Phase F5 — mask layer. Renders ON TOP of the face features but
 *  UNDER the hat (z-order: background → body → face → MASK → hat →
 *  border). Each entry returns SVG children + an optional className
 *  for CSS animation. */
export interface MaskDef {
  id: string;
  /** Display label shown in the locker / shop tile. */
  label: string;
  render(opts: CosmeticLayerOpts): CosmeticLayerOutput;
}

// ─────────────────────────────────────────────────────────────────────
// Phase E3 — Border + Background entries
// ─────────────────────────────────────────────────────────────────────
//
// Each border renders into a wrapping <g> drawn AFTER all other layers
// (so the ring sits on top). Each background renders into a wrapping
// <g> drawn BEFORE the body silhouette. Both receive the player's
// resolved primaryHue + accentHue so themed items can tint themselves
// to match the wearer.
//
// Animation policy: CSS-only. Each registry entry sets a `className` on
// its wrapping <g>; matching keyframes live in `web/src/styles/global.css`
// under the `/* Cosmetic animations */` section. We never animate
// layout properties — only `transform`, `opacity`, `filter: hue-rotate`.

/** Border ring overlays. 5 lantern-shop entries + ember-ring (Crimson
 *  Solstice tier 30 premium). */
export const BORDERS: BorderDef[] = [
  // 1. leaf-ring — circle of stylized leaves around the avatar
  //    perimeter. Static. Rarity 2. lantern-shop 400.
  {
    id: 'leaf-ring',
    render: ({ accentHue }) => {
      // 12 leaves placed around a circle of radius 47, each a tear-drop
      // path rotated to face outward. Color picks up accent hue so the
      // border harmonizes with the avatar's accent palette.
      const leafFill = `hsl(${(accentHue + 100) % 360} 55% 38%)`;
      const leafEdge = `hsl(${(accentHue + 100) % 360} 60% 22%)`;
      const leaves = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        return (
          <g key={`leaf-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 3 C 47 6, 47 10, 50 13 C 53 10, 53 6, 50 3 Z"
              fill={leafFill}
              stroke={leafEdge}
              strokeWidth="0.5"
            />
          </g>
        );
      });
      return { children: <>{leaves}</>, className: 'border-leaf-ring' };
    },
  },

  // 2. brass-frame — thick metallic ring with rivets.
  //    Slight gradient highlight. Rarity 2. lantern-shop 500.
  {
    id: 'brass-frame',
    render: () => {
      const gradId = 'brass-frame-grad';
      const rivets = Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 360) / 8;
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad) * 47;
        const cy = 50 + Math.sin(rad) * 47;
        return <circle key={`rivet-${i}`} cx={cx} cy={cy} r={1.4} fill="#5a4a20" />;
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f5d68a" />
                <stop offset="50%" stopColor="#b8902a" />
                <stop offset="100%" stopColor="#6c5012" />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="47" fill="none" stroke={`url(#${gradId})`} strokeWidth="6" />
            {rivets}
          </>
        ),
        className: 'border-brass-frame',
      };
    },
  },

  // 3. candle-circle — 8 small candle flames evenly distributed.
  //    Each flame opacity wobble, staggered. Rarity 3. lantern-shop 800.
  {
    id: 'candle-circle',
    render: () => {
      // 8 flame tongues. We only have 5 stagger phases (.flame-A..E) per
      // the spec, so flames cycle through A B C D E A B C — gives a
      // pleasant drift without per-flame keyframes.
      const phases = ['A', 'B', 'C', 'D', 'E', 'A', 'B', 'C'];
      const flames = Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 360) / 8;
        return (
          <g key={`flame-${i}`} transform={`rotate(${angle} 50 50)`} className={`flame flame-${phases[i]}`}>
            {/* candle base */}
            <rect x="48.5" y="2" width="3" height="4" fill="#f5d68a" />
            {/* flame */}
            <path
              d="M50 -2 C 47 1, 47 5, 50 6 C 53 5, 53 1, 50 -2 Z"
              fill="#ffaa3d"
              stroke="#ff7020"
              strokeWidth="0.4"
            />
          </g>
        );
      });
      return { children: <>{flames}</>, className: 'border-candle-circle' };
    },
  },

  // 4. gemstone-studded — gold ring with 4 inset gem stones.
  //    Rarity 3. lantern-shop 900.
  {
    id: 'gemstone-studded',
    render: ({ accentHue }) => {
      const gemColor = `hsl(${accentHue} 80% 60%)`;
      const gemEdge = `hsl(${accentHue} 70% 30%)`;
      // 4 gems at cardinal points, each a diamond shape.
      const gems = [0, 90, 180, 270].map((angle) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad) * 47;
        const cy = 50 + Math.sin(rad) * 47;
        return (
          <g key={`gem-${angle}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d={`M${cx} ${cy - 4} L ${cx + 3} ${cy} L ${cx} ${cy + 4} L ${cx - 3} ${cy} Z`}
              fill={gemColor}
              stroke={gemEdge}
              strokeWidth="0.5"
            />
          </g>
        );
      });
      return {
        children: (
          <>
            <circle cx="50" cy="50" r="47" fill="none" stroke="#ddc090" strokeWidth="3.5" />
            {gems}
          </>
        ),
        className: 'border-gemstone-studded',
      };
    },
  },

  // 5. runic-glyph — engraved arcane runes around the ring,
  //    rotating slowly via CSS @keyframes. Rarity 4. lantern-shop 1500.
  {
    id: 'runic-glyph',
    render: ({ primaryHue }) => {
      const stoneColor = `hsl(${primaryHue} 12% 22%)`;
      const runeColor = `hsl(${(primaryHue + 30) % 360} 70% 70%)`;
      // 12 runes around the ring, each a tiny stylized angular glyph.
      // Rotation is on the inner <g.runes> wrapper (separate from the
      // outer stone ring which stays still).
      const runes = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        // Three rune shapes alternating — gives variety without 12 paths.
        const variant = i % 3;
        const path =
          variant === 0
            ? 'M-1.5 -3 L 1.5 -3 M 0 -3 L 0 3 M -1.5 3 L 1.5 3'
            : variant === 1
              ? 'M-2 -3 L 0 0 L -2 3 M 0 -3 L 2 0 L 0 3'
              : 'M-2 -3 L 2 -3 L 0 0 L 2 3 L -2 3';
        return (
          <g key={`rune-${i}`} transform={`rotate(${angle} 50 50) translate(50 4)`}>
            <path d={path} fill="none" stroke={runeColor} strokeWidth="0.7" strokeLinecap="round" />
          </g>
        );
      });
      return {
        children: (
          <>
            <circle cx="50" cy="50" r="47" fill="none" stroke={stoneColor} strokeWidth="6" />
            <g className="runes">{runes}</g>
          </>
        ),
        className: 'border-runic-glyph',
      };
    },
  },

  // 6. ember-ring — CRIMSON SOLSTICE TIER 30, premium-only.
  //    Mythic rework: 4 layered animations — two counter-rotating flame
  //    rings (outer big tongues + inner short tongues), a pulsing inner
  //    glow halo, and 6 seam-sparks that flicker on staggered phases.
  {
    id: 'ember-ring',
    render: () => {
      // OUTER ring — 16 tall flame tongues, slow CW rotation + hue-shift.
      const outerFlames = Array.from({ length: 16 }).map((_, i) => {
        const angle = (i * 360) / 16;
        const fill = i % 2 === 0 ? '#ff4040' : '#ff7820';
        return (
          <g key={`o-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 1 C 46 6, 46 12, 50 18 C 54 12, 54 6, 50 1 Z"
              fill={fill}
              stroke="#4a0a0a"
              strokeWidth="0.4"
            />
          </g>
        );
      });
      // INNER ring — 12 shorter tongues, counter-rotating CCW + scale-pulse.
      const innerFlames = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        return (
          <g key={`i-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 8 C 48 12, 48 16, 50 19 C 52 16, 52 12, 50 8 Z"
              fill="#ffaa3d"
              opacity="0.85"
            />
          </g>
        );
      });
      // 6 sparks at the seam between rings. Stagger via phase A/B/C.
      const sparks = [0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad - Math.PI / 2) * 42;
        const cy = 50 + Math.sin(rad - Math.PI / 2) * 42;
        const phase = ['A', 'B', 'C'][i % 3];
        return <circle key={`sp-${i}`} cx={cx} cy={cy} r="1.2" fill="#ffe6a8" className={`spark spark-${phase}`} />;
      });
      return {
        children: (
          <>
            {/* Outermost trim — deep blood */}
            <circle cx="50" cy="50" r="49" fill="none" stroke="#4a0a0a" strokeWidth="1.5" />
            {/* Outer animated flame ring (CW rotate + hue-shift) */}
            <g className="outer-flame">{outerFlames}</g>
            {/* Inner animated flame ring (CCW rotate + scale-pulse) */}
            <g className="inner-flame">{innerFlames}</g>
            {/* Pulsing glow halo — soft orange ring */}
            <circle cx="50" cy="50" r="46" fill="none" stroke="#ffaa3d" strokeWidth="1.4" className="glow-halo" />
            {/* Sparks at the seam */}
            <g className="sparks">{sparks}</g>
          </>
        ),
        className: 'border-ember-ring',
      };
    },
  },

  // ───────── Phase F6 additions — 6 new avatar borders ─────────
  // Bug-2 fix — F5 only added new SEAT borders; the avatar BORDERS
  // registry was stuck at 6. Players had nothing new to equip in the
  // avatar Border slot and the docs hint at obsidian / void-ring /
  // tide-foam / phoenix lines. These six fill that gap.

  // 7. obsidian-band — black band with subtle red veins. Rarity 3.
  {
    id: 'obsidian-band',
    render: () => {
      const gradId = 'border-obsidian-band-grad';
      // Three short crimson veins — drawn as faint angular lines that
      // overlay the obsidian band. Static for now (rarity 3 policy:
      // subtle, not animated).
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#1a0e14" />
                <stop offset="50%"  stopColor="#08040a" />
                <stop offset="100%" stopColor="#1a0e14" />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="47" fill="none" stroke={`url(#${gradId})`} strokeWidth="5" />
            {/* veins — short red arcs at random spots */}
            <path d="M 6 52 Q 12 56 14 50" fill="none" stroke="#a02418" strokeWidth="0.7" strokeLinecap="round" />
            <path d="M 92 46 Q 86 42 84 48" fill="none" stroke="#a02418" strokeWidth="0.7" strokeLinecap="round" />
            <path d="M 50 4  Q 54 8 50 12" fill="none" stroke="#a02418" strokeWidth="0.7" strokeLinecap="round" />
          </>
        ),
        className: 'border-obsidian-band',
      };
    },
  },

  // 8. crystal-shard — refracting crystal facets, prismatic.
  //    Rarity 4 rework. Three layers — outer shards rotating CW, inner
  //    shards rotating CCW (parallax), conic-gradient hue-rotate stripe
  //    via CSS, plus a refracted glow circle.
  {
    id: 'crystal-shard',
    render: () => {
      // OUTER — 10 long shards
      const outer = Array.from({ length: 10 }).map((_, i) => {
        const angle = (i * 360) / 10;
        const fill = i % 2 === 0 ? '#9be0f0' : '#c0d8ff';
        return (
          <g key={`os-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 0 L 45 12 L 55 12 Z"
              fill={fill}
              stroke="#3a6090"
              strokeWidth="0.5"
              opacity="0.95"
            />
          </g>
        );
      });
      // INNER — 8 smaller shards offset rotation, parallax counter-spin
      const inner = Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 360) / 8 + 22;
        const fill = i % 2 === 0 ? '#e0f0ff' : '#a8c8ff';
        return (
          <g key={`is-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 6 L 47 14 L 53 14 Z"
              fill={fill}
              stroke="#5078a0"
              strokeWidth="0.3"
              opacity="0.9"
            />
          </g>
        );
      });
      return {
        children: (
          <>
            <circle cx="50" cy="50" r="48" fill="none" stroke="#3a6090" strokeWidth="1.5" />
            {/* outer shards — CW rotation + full-spectrum hue-rotate */}
            <g className="shards-outer">{outer}</g>
            {/* inner shards — CCW rotation, slower */}
            <g className="shards-inner">{inner}</g>
            {/* refracted glow ring — pulsing prismatic halo */}
            <circle cx="50" cy="50" r="46" fill="none" stroke="#c0e0ff" strokeWidth="0.8" className="refract-glow" />
          </>
        ),
        className: 'border-crystal-shard',
      };
    },
  },

  // 9. tide-foam-ring — wave / foam ring with drift. Rarity 3.
  {
    id: 'tide-foam-ring',
    render: () => {
      // 12 small foam crests evenly spaced. Each is a soft semi-circle
      // bump on the ring perimeter. The wrapping <g.foam> drifts via a
      // gentle translateY.
      const crests = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        return (
          <g key={`crest-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M47 5 Q 50 1 53 5 Q 50 7 47 5 Z"
              fill="#dceefc"
              stroke="#5a8ab0"
              strokeWidth="0.4"
            />
          </g>
        );
      });
      return {
        children: (
          <>
            <circle cx="50" cy="50" r="46" fill="none" stroke="#3a6c8a" strokeWidth="3" />
            <g className="foam">{crests}</g>
          </>
        ),
        className: 'border-tide-foam-ring',
      };
    },
  },

  // 10. void-eclipse — event horizon. Mythic rarity 5 rework.
  //     Layered: deep black core, accretion-disc ring with chromatic
  //     hue-rotate aberration, twinkling micro-stars (3 stagger phases),
  //     and a slow rotation on the disc.
  {
    id: 'void-eclipse',
    render: () => {
      // Twinkling micro-stars at varied positions / radii / phases.
      const stars: Array<{ cx: number; cy: number; r: number; phase: 'A' | 'B' | 'C' }> = [
        { cx: 50, cy: 3,  r: 0.9, phase: 'A' },
        { cx: 96, cy: 50, r: 1.1, phase: 'B' },
        { cx: 50, cy: 97, r: 0.9, phase: 'C' },
        { cx: 4,  cy: 50, r: 1.1, phase: 'A' },
        { cx: 80, cy: 18, r: 0.7, phase: 'B' },
        { cx: 82, cy: 82, r: 0.7, phase: 'C' },
        { cx: 18, cy: 82, r: 0.7, phase: 'A' },
        { cx: 18, cy: 18, r: 0.7, phase: 'B' },
        { cx: 92, cy: 32, r: 0.5, phase: 'C' },
        { cx: 8,  cy: 32, r: 0.5, phase: 'A' },
        { cx: 92, cy: 68, r: 0.5, phase: 'B' },
        { cx: 8,  cy: 68, r: 0.5, phase: 'C' },
      ];
      // Accretion disc — 24 short bright tick-marks at the perimeter,
      // wrapped in a single <g.accretion> that rotates + hue-rotates.
      const disc = Array.from({ length: 24 }).map((_, i) => {
        const angle = (i * 360) / 24;
        return (
          <g key={`ad-${i}`} transform={`rotate(${angle} 50 50)`}>
            <rect x="49.4" y="2" width="1.2" height="3.4" fill="#dcc8ff" opacity="0.85" />
          </g>
        );
      });
      return {
        children: (
          <>
            {/* Outer fixed black band — the event horizon edge */}
            <circle cx="50" cy="50" r="49" fill="none" stroke="#000000" strokeWidth="2" />
            <circle cx="50" cy="50" r="46" fill="#04020a" stroke="#1a1028" strokeWidth="1.2" />
            {/* Accretion disc — bright, chromatic, rotates */}
            <g className="accretion">{disc}</g>
            {/* Inner soft glow ring */}
            <circle cx="50" cy="50" r="44" fill="none" stroke="#6a4ab8" strokeWidth="0.6" className="event-glow" />
            {/* Twinkling micro-stars — 3 stagger phases */}
            <g className="starlight">
              {stars.map((s, i) => (
                <circle
                  key={`vs-${i}`}
                  cx={s.cx}
                  cy={s.cy}
                  r={s.r}
                  fill="#ffffff"
                  className={`vstar vstar-${s.phase}`}
                />
              ))}
            </g>
          </>
        ),
        className: 'border-void-eclipse',
      };
    },
  },

  // 11. phoenix-feather — animated red/gold feather ring. Rarity 4.
  //     Rework: layered plumes that wave (scale-pulse stagger phases),
  //     hue-rotate red→gold shimmer, plus rising ember sparks at 6
  //     positions with stagger phases.
  {
    id: 'phoenix-feather',
    render: () => {
      // 12 plumes in 3 wave phases (A/B/C) so the ring undulates instead
      // of pulsing in unison.
      const plumes = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        const fill = i % 2 === 0 ? '#ff5a28' : '#f5c050';
        const phase = ['A', 'B', 'C'][i % 3];
        return (
          <g key={`pl-${i}`} transform={`rotate(${angle} 50 50)`} className={`plume plume-${phase}`}>
            <path
              d="M50 1 C 46 6, 46 14, 50 18 C 54 14, 54 6, 50 1 Z"
              fill={fill}
              stroke="#5a1a08"
              strokeWidth="0.4"
            />
          </g>
        );
      });
      // Ember sparks — 6 small dots that rise & fade, staggered.
      const embers = [0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad - Math.PI / 2) * 24;
        const cy = 50 + Math.sin(rad - Math.PI / 2) * 24;
        const phase = ['A', 'B', 'C'][i % 3];
        return <circle key={`pe-${i}`} cx={cx} cy={cy} r="1" fill="#ffe6a8" className={`pember pember-${phase}`} />;
      });
      return {
        children: (
          <>
            <circle cx="50" cy="50" r="48" fill="none" stroke="#a04020" strokeWidth="1.2" />
            {/* plumes — wrapping <g.plumes> hue-rotates the whole ring */}
            <g className="plumes">{plumes}</g>
            {/* rising ember sparks */}
            <g className="pembers">{embers}</g>
          </>
        ),
        className: 'border-phoenix-feather',
      };
    },
  },

  // 12. gilded-rose-ring — gold filigree with rose-gold accents.
  //     Rarity 4. Static (filigree reads ornate already).
  {
    id: 'gilded-rose-ring',
    render: () => {
      // 6 rose buds at hex points + connecting filigree arcs.
      const roses = [0, 60, 120, 180, 240, 300].map((angle) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad - Math.PI / 2) * 47;
        const cy = 50 + Math.sin(rad - Math.PI / 2) * 47;
        return (
          <g key={`rose-${angle}`}>
            <circle cx={cx} cy={cy} r={2.2} fill="#f5b09c" stroke="#a05878" strokeWidth="0.4" />
            <circle cx={cx} cy={cy} r={1.0} fill="#f5d0c0" />
          </g>
        );
      });
      return {
        children: (
          <>
            {/* Outer gold filigree ring */}
            <circle cx="50" cy="50" r="47" fill="none" stroke="#e8c870" strokeWidth="2.5" />
            {/* Inner rose-gold trim */}
            <circle cx="50" cy="50" r="43" fill="none" stroke="#d8a888" strokeWidth="0.8" />
            {roses}
          </>
        ),
        className: 'border-gilded-rose-ring',
      };
    },
  },
];

/** Background plates. 3 lantern-shop entries + forges-maw (Crimson
 *  Solstice tier 50 premium). */
export const BACKGROUNDS: BackgroundDef[] = [
  // 1. tavern-hearth — warm radial gradient (orange center → dark edge).
  //    Static. Rarity 2. lantern-shop 500.
  {
    id: 'tavern-hearth',
    render: () => {
      const gradId = 'bg-tavern-hearth-grad';
      return {
        children: (
          <>
            <defs>
              <radialGradient id={gradId} cx="50%" cy="55%" r="50%">
                <stop offset="0%" stopColor="#ffaa3d" />
                <stop offset="55%" stopColor="#a04020" />
                <stop offset="100%" stopColor="#1a0808" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
          </>
        ),
        className: 'background-tavern-hearth',
      };
    },
  },

  // 2. forest-grove — green linearGradient with 3 floating leaves.
  //    Leaves drift translateY. Rarity 3. lantern-shop 800.
  {
    id: 'forest-grove',
    render: () => {
      const gradId = 'bg-forest-grove-grad';
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3a6028" />
                <stop offset="100%" stopColor="#0e2008" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            <g className="leaf leaf-A">
              <path d="M22 28 C 18 32, 18 36, 22 38 C 26 36, 26 32, 22 28 Z" fill="#5a8a3a" opacity="0.7" />
            </g>
            <g className="leaf leaf-B">
              <path d="M78 50 C 74 54, 74 58, 78 60 C 82 58, 82 54, 78 50 Z" fill="#6ea042" opacity="0.7" />
            </g>
            <g className="leaf leaf-C">
              <path d="M40 76 C 36 80, 36 84, 40 86 C 44 84, 44 80, 40 76 Z" fill="#4a7830" opacity="0.7" />
            </g>
          </>
        ),
        className: 'background-forest-grove',
      };
    },
  },

  // 3. starfield — deep cosmos with 3-depth parallax stars + nebula
  //    drift + shooting-star streaks. Rarity 4 rework. lantern-shop 1500.
  {
    id: 'starfield',
    render: () => {
      const gradId = 'bg-starfield-grad';
      const nebId  = 'bg-starfield-neb';
      // FAR small stars (twinkle fast, stagger 5 phases)
      const farStars: Array<{ cx: number; cy: number; r: number; phase: string }> = [
        { cx: 12, cy: 22, r: 0.6, phase: 'A' },
        { cx: 24, cy: 14, r: 0.5, phase: 'B' },
        { cx: 38, cy: 30, r: 0.6, phase: 'C' },
        { cx: 52, cy: 12, r: 0.5, phase: 'D' },
        { cx: 64, cy: 26, r: 0.7, phase: 'E' },
        { cx: 78, cy: 16, r: 0.5, phase: 'A' },
        { cx: 88, cy: 34, r: 0.6, phase: 'C' },
        { cx: 14, cy: 64, r: 0.5, phase: 'B' },
        { cx: 30, cy: 76, r: 0.6, phase: 'D' },
        { cx: 50, cy: 70, r: 0.5, phase: 'E' },
        { cx: 72, cy: 74, r: 0.6, phase: 'A' },
        { cx: 86, cy: 86, r: 0.5, phase: 'C' },
      ];
      // MID stars (slower twinkle)
      const midStars: Array<{ cx: number; cy: number; r: number; phase: string }> = [
        { cx: 22, cy: 44, r: 1.0, phase: 'A' },
        { cx: 60, cy: 50, r: 1.1, phase: 'C' },
        { cx: 82, cy: 58, r: 0.9, phase: 'E' },
        { cx: 36, cy: 84, r: 1.0, phase: 'B' },
      ];
      // NEAR stars (large, slow pulse)
      const nearStars: Array<{ cx: number; cy: number; r: number; phase: string }> = [
        { cx: 70, cy: 38, r: 1.7, phase: 'A' },
        { cx: 28, cy: 60, r: 1.6, phase: 'C' },
      ];
      return {
        children: (
          <>
            <defs>
              <radialGradient id={gradId} cx="50%" cy="50%" r="60%">
                <stop offset="0%" stopColor="#1a2454" />
                <stop offset="100%" stopColor="#04081a" />
              </radialGradient>
              <radialGradient id={nebId} cx="40%" cy="60%" r="55%">
                <stop offset="0%" stopColor="#5a3a98" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#5a3a98" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* drifting nebula plate — hue-rotate animation */}
            <g className="sf-neb">
              <ellipse cx="50" cy="50" rx="45" ry="32" fill={`url(#${nebId})`} />
            </g>
            {/* FAR stars layer — fast twinkle */}
            <g className="sf-far">
              {farStars.map((s, i) => (
                <circle key={`fs-${i}`} cx={s.cx} cy={s.cy} r={s.r} fill="#fff8d8" className={`star star-${s.phase}`} />
              ))}
            </g>
            {/* MID stars layer — medium twinkle */}
            <g className="sf-mid">
              {midStars.map((s, i) => (
                <circle key={`ms-${i}`} cx={s.cx} cy={s.cy} r={s.r} fill="#dcc8ff" className={`star star-${s.phase}`} />
              ))}
            </g>
            {/* NEAR stars layer — slow large pulse */}
            <g className="sf-near">
              {nearStars.map((s, i) => (
                <circle key={`ns-${i}`} cx={s.cx} cy={s.cy} r={s.r} fill="#ffffff" className={`star star-${s.phase}`} />
              ))}
            </g>
            {/* Shooting star — long thin streak that translates across */}
            <g className="sf-shoot">
              <path d="M -10 30 L 14 22" stroke="#ffffff" strokeWidth="0.8" strokeLinecap="round" opacity="0.9" />
            </g>
          </>
        ),
        className: 'background-starfield',
      };
    },
  },

  // 4. forges-maw — CRIMSON SOLSTICE TIER 50, premium-only.
  //    Mythic rarity-5 rework. Layered: throbbing crimson core, drifting
  //    smoke wisps, rising embers (5 with stagger phases), pulsing
  //    hot-spot coals.
  {
    id: 'forges-maw',
    render: () => {
      const gradId = 'bg-forges-maw-grad';
      const coreId = 'bg-forges-maw-core';
      const smokeId = 'bg-forges-maw-smoke';
      // 5 rising embers with staggered fade phases.
      const embers: Array<{ cx: number; cy: number; r: number; phase: string }> = [
        { cx: 22, cy: 70, r: 0.9, phase: 'A' },
        { cx: 38, cy: 82, r: 0.7, phase: 'B' },
        { cx: 56, cy: 74, r: 0.8, phase: 'C' },
        { cx: 70, cy: 86, r: 0.7, phase: 'D' },
        { cx: 84, cy: 76, r: 0.9, phase: 'E' },
      ];
      return {
        children: (
          <>
            <defs>
              <radialGradient id={gradId} cx="50%" cy="55%" r="55%">
                <stop offset="0%" stopColor="#4a0a0a" />
                <stop offset="55%" stopColor="#280404" />
                <stop offset="100%" stopColor="#0a0202" />
              </radialGradient>
              <radialGradient id={coreId} cx="50%" cy="60%" r="40%">
                <stop offset="0%" stopColor="#ff7820" stopOpacity="0.85" />
                <stop offset="60%" stopColor="#a02410" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#4a0a0a" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={smokeId} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#3a2018" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#3a2018" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* throbbing crimson core */}
            <g className="forge-core">
              <ellipse cx="50" cy="60" rx="34" ry="26" fill={`url(#${coreId})`} />
            </g>
            {/* drifting smoke wisp #1 */}
            <g className="forge-smoke-A">
              <ellipse cx="30" cy="32" rx="22" ry="10" fill={`url(#${smokeId})`} />
            </g>
            {/* drifting smoke wisp #2 — opposite phase */}
            <g className="forge-smoke-B">
              <ellipse cx="70" cy="22" rx="20" ry="8" fill={`url(#${smokeId})`} />
            </g>
            {/* hot coals — pulse on stagger phases */}
            <circle cx="28" cy="64" r="3.4" fill="#ffaa3d" className="coal coal-A" />
            <circle cx="60" cy="76" r="2.8" fill="#ff7820" className="coal coal-B" />
            <circle cx="76" cy="50" r="3.0" fill="#ffaa3d" className="coal coal-C" />
            {/* rising embers */}
            <g className="forge-embers">
              {embers.map((e, i) => (
                <circle key={`fe-${i}`} cx={e.cx} cy={e.cy} r={e.r} fill="#ffe6a8" className={`fember fember-${e.phase}`} />
              ))}
            </g>
          </>
        ),
        className: 'background-forges-maw',
      };
    },
  },

  // ───────── Phase F6 additions — 4 new avatar backgrounds ─────────
  // Bug-2 companion fix — F5 only added new SEAT backgrounds; the
  // avatar BACKGROUNDS registry was stuck at 4. These four widen the
  // selection so players who buy a non-Solstice background actually
  // have variety in the locker.

  // 5. parchment-scroll — warm cream parchment with faint ink lines.
  //    Static. Rarity 2.
  {
    id: 'parchment-scroll',
    render: () => {
      const gradId = 'bg-parchment-scroll-grad';
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#f5e6c4" />
                <stop offset="100%" stopColor="#c8a878" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* faint ink horizontal lines for parchment feel */}
            <path d="M 8 30 L 92 30" stroke="#8a6a3a" strokeWidth="0.3" opacity="0.35" />
            <path d="M 8 50 L 92 50" stroke="#8a6a3a" strokeWidth="0.3" opacity="0.35" />
            <path d="M 8 70 L 92 70" stroke="#8a6a3a" strokeWidth="0.3" opacity="0.35" />
          </>
        ),
        className: 'background-parchment-scroll',
      };
    },
  },

  // 6. velvet-drape — deep purple curtain with vertical highlight.
  //    Static. Rarity 3.
  {
    id: 'velvet-drape',
    render: () => {
      const gradId = 'bg-velvet-drape-grad';
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="#2a0e44" />
                <stop offset="50%"  stopColor="#5a2e88" />
                <stop offset="100%" stopColor="#2a0e44" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* drape folds — soft vertical highlight bands */}
            <rect x="28" y="0" width="3" height="100" fill="#7a4ea0" opacity="0.35" />
            <rect x="68" y="0" width="3" height="100" fill="#7a4ea0" opacity="0.35" />
          </>
        ),
        className: 'background-velvet-drape',
      };
    },
  },

  // 7. tide-pool-wash — soft aqua / teal with rippling highlights.
  //    Animated rarity 3.
  {
    id: 'tide-pool-wash',
    render: () => {
      const gradId = 'bg-tide-pool-wash-grad';
      return {
        children: (
          <>
            <defs>
              <radialGradient id={gradId} cx="50%" cy="50%" r="70%">
                <stop offset="0%"   stopColor="#9be0d8" />
                <stop offset="60%"  stopColor="#3a8a9e" />
                <stop offset="100%" stopColor="#0e2c3c" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* 3 ripple ovals — staggered phases ripple-A/B/C */}
            <ellipse cx="36" cy="44" rx="8" ry="2" fill="none" stroke="#cfeef0" strokeWidth="0.5" opacity="0.6" className="ripple ripple-A" />
            <ellipse cx="64" cy="60" rx="10" ry="2.5" fill="none" stroke="#cfeef0" strokeWidth="0.5" opacity="0.6" className="ripple ripple-B" />
            <ellipse cx="50" cy="76" rx="9" ry="2" fill="none" stroke="#cfeef0" strokeWidth="0.5" opacity="0.6" className="ripple ripple-C" />
          </>
        ),
        className: 'background-tide-pool-wash',
      };
    },
  },

  // 8. cosmos-veil — galaxy. Rarity 4 rework. Layered: rotating
  //    pink/blue nebula, scattered stars at 3 depths twinkling on
  //    stagger phases, slow breathing radial glow.
  {
    id: 'cosmos-veil',
    render: () => {
      const gradId = 'bg-cosmos-veil-grad';
      const nebId  = 'bg-cosmos-veil-neb';
      const nebBId = 'bg-cosmos-veil-neb-b';
      const glowId = 'bg-cosmos-veil-glow';
      return {
        children: (
          <>
            <defs>
              <radialGradient id={gradId} cx="50%" cy="50%" r="70%">
                <stop offset="0%"   stopColor="#2a1858" />
                <stop offset="100%" stopColor="#04081a" />
              </radialGradient>
              {/* primary nebula — magenta */}
              <radialGradient id={nebId} cx="40%" cy="45%" r="55%">
                <stop offset="0%"   stopColor="#d050b8" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#d050b8" stopOpacity="0" />
              </radialGradient>
              {/* secondary nebula — cyan/blue */}
              <radialGradient id={nebBId} cx="65%" cy="60%" r="45%">
                <stop offset="0%"   stopColor="#5078ff" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#5078ff" stopOpacity="0" />
              </radialGradient>
              {/* breathing radial glow */}
              <radialGradient id={glowId} cx="50%" cy="50%" r="55%">
                <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* breathing glow */}
            <g className="cv-breath">
              <ellipse cx="50" cy="50" rx="48" ry="38" fill={`url(#${glowId})`} />
            </g>
            {/* primary nebula — slow CW rotation + hue-rotate */}
            <g className="cv-neb-A">
              <ellipse cx="50" cy="50" rx="40" ry="30" fill={`url(#${nebId})`} />
            </g>
            {/* secondary nebula — slow CCW rotation, opposite hue */}
            <g className="cv-neb-B">
              <ellipse cx="50" cy="50" rx="34" ry="26" fill={`url(#${nebBId})`} />
            </g>
            {/* near (large) stars */}
            <g className="cv-near">
              <circle cx="24" cy="22" r="1.2" fill="#fff8d8" className="star star-A" />
              <circle cx="78" cy="80" r="1.3" fill="#dcc8ff" className="star star-C" />
            </g>
            {/* mid stars */}
            <g className="cv-mid">
              <circle cx="86" cy="30" r="0.9" fill="#dcc8ff" className="star star-B" />
              <circle cx="14" cy="74" r="0.8" fill="#fff8d8" className="star star-D" />
              <circle cx="50" cy="86" r="0.9" fill="#dcc8ff" className="star star-E" />
            </g>
            {/* far stars */}
            <g className="cv-far">
              <circle cx="14" cy="22" r="0.5" fill="#fff8d8" className="star star-A" />
              <circle cx="36" cy="60" r="0.5" fill="#dcc8ff" className="star star-C" />
              <circle cx="68" cy="14" r="0.5" fill="#fff8d8" className="star star-E" />
              <circle cx="92" cy="56" r="0.5" fill="#dcc8ff" className="star star-B" />
              <circle cx="50" cy="40" r="0.4" fill="#fff8d8" className="star star-D" />
            </g>
          </>
        ),
        className: 'background-cosmos-veil',
      };
    },
  },
];

// ─────────────────────────────────────────────────────────────────────
// Phase F5 — Masks (face-overlay cosmetic family)
// ─────────────────────────────────────────────────────────────────────
//
// Masks render ON TOP of face features but UNDER the hat (z-order:
// background → body → face → MASK → hat → border). All paths sized to
// the same 100×100 SVG canvas the body / face / hat use; the eye region
// for FACE_SHAPES sits around y=50, so masks frame that area. Each mask
// reads as a half-mask covering the upper face — leaves the mouth
// readable. Animations apply via CSS classes in the cosmetic-animations
// section of `web/src/styles/global.css`. Most masks are static; mythic
// ones get a subtle shimmer/pulse to read as rare without spamming
// motion across a 4-seat lobby.
export const MASKS: MaskDef[] = [
  // 1. domino — simple black eye-mask. Static. Rarity 2 / lantern-shop.
  {
    id: 'domino',
    label: 'Domino mask',
    render: () => ({
      children: (
        <path
          d="M22 44 C 22 38, 32 36, 42 38 L 50 40 L 58 38 C 68 36, 78 38, 78 44 C 78 56, 64 60, 54 56 L 50 54 L 46 56 C 36 60, 22 56, 22 44 Z"
          fill="#0a0710"
          stroke="#1a1018"
          strokeWidth="0.6"
        />
      ),
    }),
  },

  // 2. feline — cat-eye mask with whisker hints. Static. Rarity 3.
  {
    id: 'feline',
    label: 'Feline mask',
    render: () => ({
      children: (
        <>
          <path
            d="M22 44 C 22 36, 32 32, 42 36 L 48 40 L 52 40 L 58 36 C 68 32, 78 36, 78 44 C 78 50, 70 58, 60 56 C 56 54, 52 50, 50 50 C 48 50, 44 54, 40 56 C 30 58, 22 50, 22 44 Z"
            fill="#1a0e16"
            stroke="#3a1830"
            strokeWidth="0.6"
          />
          {/* whisker dots */}
          <circle cx="32" cy="60" r="0.8" fill="#3a1830" />
          <circle cx="36" cy="62" r="0.8" fill="#3a1830" />
          <circle cx="64" cy="62" r="0.8" fill="#3a1830" />
          <circle cx="68" cy="60" r="0.8" fill="#3a1830" />
        </>
      ),
    }),
  },

  // 3. harlequin — diamond-pattern half mask. Static. Rarity 3.
  {
    id: 'harlequin',
    label: 'Harlequin mask',
    render: () => ({
      children: (
        <>
          <path
            d="M22 44 C 22 36, 32 36, 42 38 L 50 40 L 58 38 C 68 36, 78 36, 78 44 C 78 56, 66 60, 54 56 L 50 54 L 46 56 C 34 60, 22 56, 22 44 Z"
            fill="#a83a52"
            stroke="#5a1a28"
            strokeWidth="0.6"
          />
          {/* diamond pattern overlay */}
          <path d="M30 44 L 34 40 L 38 44 L 34 48 Z" fill="#f1d780" />
          <path d="M42 48 L 46 44 L 50 48 L 46 52 Z" fill="#f1d780" />
          <path d="M54 44 L 58 40 L 62 44 L 58 48 Z" fill="#f1d780" />
          <path d="M66 48 L 70 44 L 74 48 L 70 52 Z" fill="#f1d780" />
        </>
      ),
    }),
  },

  // 4. phantom — half mask, white with subtle glow. Animated rarity 4.
  {
    id: 'phantom',
    label: 'Phantom mask',
    render: () => ({
      children: (
        <path
          d="M22 38 C 22 32, 32 30, 42 34 L 50 38 L 50 62 L 46 60 C 36 64, 22 56, 22 38 Z"
          fill="#f4ecdc"
          stroke="#a89878"
          strokeWidth="0.5"
        />
      ),
      className: 'mask-phantom',
    }),
  },

  // 5. oni — Japanese demon mask, red with horns. Static rarity 4.
  {
    id: 'oni',
    label: 'Oni mask',
    render: () => ({
      children: (
        <>
          {/* horns */}
          <path d="M30 28 L 28 18 L 34 24 Z" fill="#e8d090" stroke="#5a3a18" strokeWidth="0.5" />
          <path d="M70 28 L 72 18 L 66 24 Z" fill="#e8d090" stroke="#5a3a18" strokeWidth="0.5" />
          {/* face plate */}
          <path
            d="M22 42 C 22 32, 32 30, 42 34 L 50 38 L 58 34 C 68 30, 78 32, 78 42 C 78 56, 66 62, 54 58 L 50 56 L 46 58 C 34 62, 22 56, 22 42 Z"
            fill="#a82018"
            stroke="#4a0808"
            strokeWidth="0.7"
          />
          {/* fang highlights */}
          <path d="M44 56 L 46 62 L 48 56 Z" fill="#f4ecdc" />
          <path d="M52 56 L 54 62 L 56 56 Z" fill="#f4ecdc" />
        </>
      ),
    }),
  },

  // 6. sunburst — radiating sun mask, gold. Animated rarity 4 rework.
  //    Long rays + short rays (alternating, opposite scale phases),
  //    pulsing inner glow ring, glowing eye slots.
  {
    id: 'sunburst',
    label: 'Sunburst mask',
    render: () => ({
      children: (
        <>
          {/* short rays — pulse on phase B (counter to long rays) */}
          <g className="mask-sunburst-rays-short">
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i * 360) / 12 + 15;
              return (
                <path
                  key={`rs-${i}`}
                  d="M50 30 L 49 22 L 51 22 Z"
                  fill="#ffe6a0"
                  transform={`rotate(${angle} 50 50)`}
                />
              );
            })}
          </g>
          {/* long rays — rotate slowly + pulse on phase A */}
          <g className="mask-sunburst-rays">
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i * 360) / 12;
              return (
                <path
                  key={`ray-${i}`}
                  d="M50 22 L 48 10 L 52 10 Z"
                  fill="#ffd770"
                  transform={`rotate(${angle} 50 50)`}
                />
              );
            })}
          </g>
          {/* inner pulsing glow ring */}
          <circle cx="50" cy="50" r="16" fill="none" stroke="#ffe6a8" strokeWidth="0.8" className="mask-sunburst-glow" />
          {/* mask face plate */}
          <circle cx="50" cy="50" r="14" fill="#ffaa3d" stroke="#a06020" strokeWidth="0.6" />
          {/* glowing eye slots — pulse */}
          <g className="mask-sunburst-eyes">
            <circle cx="42" cy="48" r="2" fill="#a06020" />
            <circle cx="58" cy="48" r="2" fill="#a06020" />
          </g>
        </>
      ),
      className: 'mask-sunburst',
    }),
  },

  // 7. void — black with deep starfield interior. Mythic rarity 5.
  //    Stars drift slowly inside the mask via a clipped translating
  //    layer; rim has a faint chromatic glow.
  {
    id: 'void',
    label: 'Void mask',
    render: () => {
      const gradId = 'mask-void-grad';
      const clipId = 'mask-void-clip';
      const maskPath = "M22 42 C 22 34, 32 32, 42 36 L 50 40 L 58 36 C 68 32, 78 34, 78 42 C 78 56, 66 60, 54 56 L 50 54 L 46 56 C 34 60, 22 56, 22 42 Z";
      return {
        children: (
          <>
            <defs>
              <radialGradient id={gradId} cx="50%" cy="50%" r="60%">
                <stop offset="0%" stopColor="#1a0828" />
                <stop offset="100%" stopColor="#04020a" />
              </radialGradient>
              <clipPath id={clipId}>
                <path d={maskPath} />
              </clipPath>
            </defs>
            {/* base mask body */}
            <path d={maskPath} fill={`url(#${gradId})`} stroke="#0a0414" strokeWidth="0.6" />
            {/* drifting starfield inside the mask, clipped */}
            <g clipPath={`url(#${clipId})`}>
              <g className="mask-void-stars">
                <circle cx="28" cy="44" r="0.6" fill="#dcc8ff" className="vmstar vmstar-A" />
                <circle cx="36" cy="48" r="0.5" fill="#f4ecdc" className="vmstar vmstar-B" />
                <circle cx="44" cy="42" r="0.7" fill="#ffffff" className="vmstar vmstar-C" />
                <circle cx="52" cy="50" r="0.4" fill="#dcc8ff" className="vmstar vmstar-A" />
                <circle cx="58" cy="44" r="0.6" fill="#f4ecdc" className="vmstar vmstar-B" />
                <circle cx="64" cy="48" r="0.5" fill="#dcc8ff" className="vmstar vmstar-C" />
                <circle cx="72" cy="44" r="0.6" fill="#ffffff" className="vmstar vmstar-A" />
                <circle cx="32" cy="52" r="0.4" fill="#dcc8ff" className="vmstar vmstar-B" />
                <circle cx="48" cy="38" r="0.5" fill="#f4ecdc" className="vmstar vmstar-C" />
                <circle cx="68" cy="52" r="0.5" fill="#ffffff" className="vmstar vmstar-A" />
              </g>
            </g>
            {/* chromatic rim glow — same path traced as stroke only */}
            <path d={maskPath} fill="none" stroke="#7a4ec8" strokeWidth="0.4" className="mask-void-rim" />
          </>
        ),
        className: 'mask-void',
      };
    },
  },

  // 8. crimson-veil — Crimson Solstice premium mythic rarity 5 rework.
  //    Layered: base mask + smoke wisp underneath, ember field that
  //    stagger-pulses, glowing eye slots, ember sparks rising from the
  //    mask edges.
  {
    id: 'crimson-veil',
    label: 'Crimson Veil mask',
    render: () => {
      const gradId = 'mask-crimson-veil-grad';
      const smokeId = 'mask-crimson-veil-smoke';
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4a0a0a" />
                <stop offset="60%" stopColor="#ff4040" />
                <stop offset="100%" stopColor="#ffaa3d" />
              </linearGradient>
              <radialGradient id={smokeId} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#3a0a0a" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#3a0a0a" stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* smoke wisp under mask */}
            <g className="mask-crimson-veil-smoke">
              <ellipse cx="50" cy="60" rx="30" ry="6" fill={`url(#${smokeId})`} />
            </g>
            {/* base mask body */}
            <path
              d="M22 36 C 22 30, 32 28, 42 32 L 50 36 L 58 32 C 68 28, 78 30, 78 36 L 76 56 L 50 60 L 24 56 Z"
              fill={`url(#${gradId})`}
              stroke="#280404"
              strokeWidth="0.7"
            />
            {/* embers — staggered phases */}
            <g className="mask-crimson-veil-embers">
              <circle cx="30" cy="48" r="1.2" fill="#ffaa3d" className="cv-ember cv-ember-A" />
              <circle cx="40" cy="46" r="1.0" fill="#ffaa3d" className="cv-ember cv-ember-B" />
              <circle cx="50" cy="48" r="1.1" fill="#ffe6a8" className="cv-ember cv-ember-C" />
              <circle cx="60" cy="46" r="1.0" fill="#ffaa3d" className="cv-ember cv-ember-A" />
              <circle cx="70" cy="48" r="1.2" fill="#ffaa3d" className="cv-ember cv-ember-B" />
            </g>
            {/* glowing eye slots */}
            <g className="mask-crimson-veil-eyes">
              <ellipse cx="40" cy="42" rx="2.2" ry="1.2" fill="#ffe6a8" />
              <ellipse cx="60" cy="42" rx="2.2" ry="1.2" fill="#ffe6a8" />
            </g>
            {/* rising sparks from mask edges */}
            <g className="mask-crimson-veil-sparks">
              <circle cx="26" cy="34" r="0.6" fill="#ffe6a8" className="cv-spark cv-spark-A" />
              <circle cx="50" cy="30" r="0.5" fill="#ffe6a8" className="cv-spark cv-spark-B" />
              <circle cx="74" cy="34" r="0.6" fill="#ffe6a8" className="cv-spark cv-spark-C" />
            </g>
          </>
        ),
        className: 'mask-crimson-veil',
      };
    },
  },

  // 9. mardi-gras — purple/gold elaborate decoration. Static rarity 3.
  {
    id: 'mardi-gras',
    label: 'Mardi Gras mask',
    render: () => ({
      children: (
        <>
          <path
            d="M22 44 C 22 36, 30 34, 40 36 L 48 40 L 52 40 L 60 36 C 70 34, 78 36, 78 44 C 78 56, 66 60, 54 56 L 50 54 L 46 56 C 34 60, 22 56, 22 44 Z"
            fill="#5a2e88"
            stroke="#2a0e44"
            strokeWidth="0.6"
          />
          {/* gold filigree dots */}
          <circle cx="28" cy="40" r="1.2" fill="#f1d780" />
          <circle cx="34" cy="38" r="1.2" fill="#f1d780" />
          <circle cx="66" cy="38" r="1.2" fill="#f1d780" />
          <circle cx="72" cy="40" r="1.2" fill="#f1d780" />
          {/* feather plumes */}
          <path d="M24 38 L 18 28 L 22 36 Z" fill="#a06ed0" stroke="#5a2e88" strokeWidth="0.4" />
          <path d="M76 38 L 82 28 L 78 36 Z" fill="#a06ed0" stroke="#5a2e88" strokeWidth="0.4" />
        </>
      ),
    }),
  },

  // 10. mirror — silver reflective mask. Mythic rarity 5 rework.
  //     Sweeping screen-glare highlight diagonally crosses the mask
  //     surface; subtle global hue-rotate so the mirror picks up faint
  //     color shifts.
  {
    id: 'mirror',
    label: 'Mirror mask',
    render: () => {
      const gradId = 'mask-mirror-grad';
      const sweepId = 'mask-mirror-sweep';
      const clipId = 'mask-mirror-clip';
      const maskPath = "M22 42 C 22 34, 32 32, 42 36 L 50 40 L 58 36 C 68 32, 78 34, 78 42 C 78 56, 66 60, 54 56 L 50 54 L 46 56 C 34 60, 22 56, 22 42 Z";
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f4f4f8" />
                <stop offset="50%" stopColor="#a8a8b8" />
                <stop offset="100%" stopColor="#dcdce4" />
              </linearGradient>
              {/* diagonal sweep highlight — bright stripe that travels */}
              <linearGradient id={sweepId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"  stopColor="#ffffff" stopOpacity="0" />
                <stop offset="45%" stopColor="#ffffff" stopOpacity="0" />
                <stop offset="50%" stopColor="#ffffff" stopOpacity="0.85" />
                <stop offset="55%" stopColor="#ffffff" stopOpacity="0" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
              <clipPath id={clipId}>
                <path d={maskPath} />
              </clipPath>
            </defs>
            {/* base mirrored surface */}
            <path d={maskPath} fill={`url(#${gradId})`} stroke="#5a5a68" strokeWidth="0.6" />
            {/* sweeping glare — clipped to mask, translates across */}
            <g clipPath={`url(#${clipId})`}>
              <g className="mask-mirror-sweep">
                <rect x="-30" y="20" width="40" height="60" fill={`url(#${sweepId})`} transform="rotate(-20 50 50)" />
              </g>
            </g>
          </>
        ),
        className: 'mask-mirror',
      };
    },
  },
];
